// Local samples folder for the Score page: point the app at a directory of
// MusicXML files (e.g. the MusicXML sample suite) ONCE and its files join the
// Examples dropdown. Chromium's File System Access API gives a persistent
// directory handle (kept in IndexedDB, so the folder reconnects on the next
// visit after a permission re-grant); other browsers fall back to a
// webkitdirectory input whose files last for the session. Either way the
// files never leave the user's machine — and never enter the repo, which is
// the standing ruling for the copyrighted reference samples.

import { MXL_LIMITS, readMxl } from '@/music/musicxml/mxl';

/** One loadable file: name + a thunk producing its MusicXML text. */
export interface SampleEntry {
  name: string;
  read: () => Promise<string>;
}

export const isScoreFileName = (name: string): boolean =>
  /\.(musicxml|xml|mxl)$/i.test(name) && !name.startsWith('.');

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

/** File → MusicXML text (.mxl is a ZIP container; anything else is XML). */
export async function readScoreFile(file: File): Promise<string> {
  if (!/\.mxl$/i.test(file.name)) return file.text();
  if (file.size > MXL_LIMITS.containerBytes) {
    throw new Error(
      `Invalid .mxl: archive exceeds the ${MXL_LIMITS.containerBytes / 1024 / 1024} MB upload limit`,
    );
  }
  return readMxl(await file.arrayBuffer());
}

// ---------------------------------------------------------------------------
// File System Access API (Chromium). The handle types below are the minimal
// surface we use — queryPermission/requestPermission are Chromium-only and
// absent from lib.dom, so we declare them rather than depend on the TS lib.
// ---------------------------------------------------------------------------

interface FileHandleLike {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
}
interface DirHandleLike {
  kind: 'directory';
  name: string;
  values(): AsyncIterable<FileHandleLike | { kind: 'directory'; name: string }>;
  queryPermission?(opts: { mode: 'read' }): Promise<PermissionState>;
  requestPermission?(opts: { mode: 'read' }): Promise<PermissionState>;
}

export const supportsFolderPicker = (): boolean =>
  typeof window !== 'undefined' && 'showDirectoryPicker' in window;

export async function pickSamplesFolder(): Promise<DirHandleLike> {
  const picker = (
    window as unknown as { showDirectoryPicker(opts?: object): Promise<DirHandleLike> }
  ).showDirectoryPicker;
  return picker({ id: 'vlc-samples', mode: 'read' });
}

/** True when the handle is readable; optionally asks the user (needs a user
 *  gesture) when the grant has lapsed since the last visit. */
export async function verifyFolderPermission(
  dir: DirHandleLike,
  request: boolean,
): Promise<boolean> {
  try {
    if ((await dir.queryPermission?.({ mode: 'read' })) === 'granted') return true;
    if (request && (await dir.requestPermission?.({ mode: 'read' })) === 'granted') return true;
  } catch {
    return false;
  }
  return false;
}

/** Every MusicXML file directly inside the folder, name-sorted. */
export async function listSamplesFolder(dir: DirHandleLike): Promise<SampleEntry[]> {
  const out: SampleEntry[] = [];
  for await (const entry of dir.values()) {
    if (entry.kind === 'file' && isScoreFileName(entry.name)) {
      const handle = entry as FileHandleLike;
      out.push({ name: entry.name, read: async () => readScoreFile(await handle.getFile()) });
    }
  }
  return out.sort(byName);
}

/** Session entries from the webkitdirectory fallback's FileList. */
export function entriesFromFileList(files: Iterable<File>): SampleEntry[] {
  const out: SampleEntry[] = [];
  for (const f of files) {
    if (isScoreFileName(f.name)) out.push({ name: f.name, read: () => readScoreFile(f) });
  }
  return out.sort(byName);
}

// ---------------------------------------------------------------------------
// Handle persistence. Directory handles are structured-cloneable in Chromium,
// so IndexedDB can store them across visits. Everything is best-effort: a
// browser without IndexedDB (or a non-cloneable handle) just loses the
// reconnect convenience, never the feature.
// ---------------------------------------------------------------------------

const DB_NAME = 'vlc-samples';
const STORE = 'handles';
const KEY = 'samples-dir';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

export async function saveFolderHandle(dir: DirHandleLike): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(dir, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB write failed'));
    });
    db.close();
  } catch {
    // Best-effort only.
  }
}

export async function loadFolderHandle(): Promise<DirHandleLike | null> {
  try {
    const db = await openDb();
    const handle = await new Promise<DirHandleLike | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as DirHandleLike | undefined) ?? null);
      req.onerror = () => reject(req.error ?? new Error('indexedDB read failed'));
    });
    db.close();
    return handle;
  } catch {
    return null;
  }
}

export async function forgetFolderHandle(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB delete failed'));
    });
    db.close();
  } catch {
    // Best-effort only.
  }
}
